import FeatureGates from '@atlaskit/feature-gate-js-client';
import { usePostOfficeContext } from '@atlassian/post-office-context';
import { usePlacementFetchingContext } from '@atlassian/post-office-placement-data';
import { usePlacementContext } from '@post-office/placement-context';
import { createUsePlacement, type InitialDataStates } from '@post-office/placement-contexts/hooks';
import type { ErrorState } from '@post-office/placement-contexts/hooks/types';
import { createUsePlacementWithClient } from '@post-office/placement-contexts/hooks/usePlacementWithClient';
import { useSubscriptionConfig } from '@post-office/placement-contexts/create-placement-component/providers/stable-graphql-subscription';
import { type SerializableRecord } from '@post-office/serializable-record';
import React, { useMemo } from 'react';

import { type Config } from './types';

// https://switcheroo.atlassian.com/ui/gates/746a161c-d307-4db3-a966-8fe63c551fea/key/post-office_skip_rest_for_graphql_subscription
const SKIP_REST_FETCH_GATE = 'post-office_skip_rest_for_graphql_subscription';

export const usePlacementApi = <T extends SerializableRecord>(
	path: string,
	config?: Config,
): InitialDataStates<T> => {
	const { placementId } = usePlacementContext();
	const context = usePostOfficeContext();
	const fetcher = usePlacementFetchingContext();
	const { subscription } = useSubscriptionConfig();

	const placementApi = React.useMemo(() => {
		if (!placementId) {
			return (): ErrorState => ({
				isLoading: false,
				data: undefined,
				error: new Error(
					'No placementId found. Ensure this hook is used as a child of PlacementProvider.',
				),
			});
		}

		// Prefer the explicit prop when provided; fall back to the Switcheroo gate
		// so existing callers that have not yet migrated continue to work unchanged.
		const isClientFetch =
			config?.useClientFetch !== undefined
				? config.useClientFetch
				: FeatureGates.checkGate('post-office-use-placement-with-client');

		if (isClientFetch) {
			return createUsePlacementWithClient(placementId, fetcher);
		}

		return createUsePlacement(placementId, fetcher);
	}, [placementId, fetcher, config?.useClientFetch]);

	const { queryParams = {} } = config ?? {};
	const placementParams = { ...queryParams, ...context };

	const apiDependencies = useMemo(() => {
		return [config?.autoRefetch?.updateKey];
	}, [config?.autoRefetch?.updateKey]);

	// disable REST fetch for initialMessages for subscription based placements if the gate is enabled
	const isSubscriptionBasedFetchDisabled =
		subscription?.enabled === true && FeatureGates.checkGate(SKIP_REST_FETCH_GATE);

	return placementApi<T, SerializableRecord>(path, placementParams, config?.requestOptions, {
		throttleTime: config?.throttleTime,
		isEnabled: isSubscriptionBasedFetchDisabled ? false : config?.autoRefetch?.isEnabled,
		deps: apiDependencies,
	});
};
